'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  EditIcon,
  TrashIcon,
  FileTextIcon,
  KeyIcon,
  HashIcon,
  LanguagesIcon,
  MapPinIcon,
  CalendarCheckIcon,
  CalendarIcon,
  CopyIcon
} from 'lucide-react'
import Flag from 'react-flagkit'
import { toast } from 'sonner'
import { formatDateTime, getCountryCodeFromLocale, getLocalizedValue } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ContentDetailFiles,
  ContentDetailInfo,
  ContentDetailPicture,
  ContentDetailPictures,
  ContentError,
  DeleteConfirmationDialog
} from '@/components/partials'
import { useMetaStore } from '@/store'
import { useEvent, useDeleteEvent, useCopyToClipboard } from '@/hooks'
import { Separator } from '@/components/ui/separator'

type PropsType = {
  id: string
}

const EventShowSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <Skeleton className="h-9 w-20" />
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Skeleton className="h-4 w-8" />
            <Skeleton className="h-6 w-32" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
)

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const locale = useLocale()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const [deleteEventId, setDeleteEventId] = useState<string | null>(null)

  const { data: event, isLoading, error } = useEvent(id)
  const { metaState } = useMetaStore()
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale
  const locales = metaState.response?.locales
  const { mutate: deleteEvent, isPending: isDeleting } = useDeleteEvent()

  const hasTranslation = (localeCode: string): boolean => {
    return event?.translations?.[localeCode] !== undefined
  }

  const handleDelete = () => {
    setDeleteEventId(id)
  }

  const confirmDeleteEvent = () => {
    if (deleteEventId) {
      deleteEvent(deleteEventId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['events'] })
          toast.success(t('deleted_successfully'))
          router.push('/events')
          setDeleteEventId(null)
        },
        onError: error => {
          console.error('Error deleting event:', error)
          toast.error(t('error_deleting'))
          setDeleteEventId(null)
        }
      })
    }
  }

  if (isLoading || isDeleting) {
    return <EventShowSkeleton />
  }

  if (error || !event) {
    return <ContentError error={error} listUrl="/events" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/events">
              <ArrowLeftIcon className="h-4 w-4" />
              <span className="hidden lg:flex">{t('list')}</span>
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('event_details')}</h1>
            <p className="text-muted-foreground">{t('view_event_details')}</p>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          <Button variant="outline" asChild>
            <Link href={`/events/${id}/edit`}>
              <EditIcon className="h-4 w-4" />
              <span className="hidden lg:flex">{t('edit_event')}</span>
            </Link>
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <TrashIcon className="h-4 w-4" />
            <span className="hidden lg:flex">{t('delete')}</span>
          </Button>
        </div>
      </div>

      {/* Event Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarCheckIcon className="h-5 w-5" />
            {t('information')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <HashIcon className="h-4 w-4" />
                ID
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="font-mono text-xs">
                  {event.id}
                </Badge>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(event.id)} className="h-6 w-6 p-0">
                      <CopyIcon className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('copy')}</TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <FileTextIcon className="h-4 w-4" />
                {t('title')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {getLocalizedValue(event, 'title', locale, defaultLocale)}
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <KeyIcon className="h-4 w-4" />
                {t('slug')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {getLocalizedValue(event, 'slug', locale, defaultLocale)}
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <MapPinIcon className="h-4 w-4" />
                {t('address')}
              </div>
              <p className="text-sm">{event.address}</p>
            </div>
            {event.latitude && event.longitude && (
              <div className="space-y-2">
                <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                  <MapPinIcon className="h-4 w-4" />
                  {t('coordinates')}
                </div>
                <p className="text-sm">
                  {event.latitude}, {event.longitude}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <CalendarIcon className="h-4 w-4" />
                {t('start_date')}
              </div>
              <div className="text-sm">{formatDateTime(event.beginDate, locale)}</div>
            </div>
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <CalendarIcon className="h-4 w-4" />
                {t('end_date')}
              </div>
              <div className="text-sm">{formatDateTime(event.endDate, locale)}</div>
            </div>
          </div>
          <Separator />
          <ContentDetailInfo entity={event} />
        </CardContent>
      </Card>

      {/* Translations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LanguagesIcon className="h-5 w-5" />
            {t('translations')}
          </CardTitle>
          <CardDescription>{t('available_translations')}</CardDescription>
        </CardHeader>
        <CardContent>
          {locales && locales.length > 0 ? (
            <Tabs defaultValue={locales[0].fullCode} className="w-full">
              <TabsList className="min-h-10.5 w-full">
                {locales.map(localeItem => (
                  <TabsTrigger
                    key={localeItem.fullCode}
                    value={localeItem.fullCode}
                    className="flex items-center gap-2"
                    disabled={!hasTranslation(localeItem.fullCode)}>
                    <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={16} />
                    <span className="hidden sm:inline">{localeItem.name}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {locales.map(localeItem => (
                <TabsContent key={localeItem.fullCode} value={localeItem.fullCode} className="mt-6 space-y-4">
                  {hasTranslation(localeItem.fullCode) ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={20} />
                        <h3 className="text-lg font-semibold">{localeItem.name}</h3>
                        <Badge variant="outline" className="text-xs">
                          {localeItem.fullCode}
                        </Badge>
                      </div>

                      <Separator />

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-muted-foreground text-sm font-medium">{t('title')}</label>
                          <div className="bg-muted/50 rounded-lg border p-4">
                            <p className="text-sm leading-relaxed">{event.translations[localeItem.fullCode].title}</p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-muted-foreground text-sm font-medium">{t('slug')}</label>
                          <div className="bg-muted/50 rounded-lg border p-4">
                            <p className="text-sm leading-relaxed">{event.translations[localeItem.fullCode].slug}</p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-muted-foreground text-sm font-medium">{t('content')}</label>
                          <div className="bg-muted/50 rounded-lg border p-4">
                            <div className="prose prose-sm dark:prose-invert html-content max-w-none">
                              {event.translations[localeItem.fullCode].content ? (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: event.translations[localeItem.fullCode].content || ''
                                  }}
                                />
                              ) : (
                                <p>-</p>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <label className="text-muted-foreground text-sm font-medium">{t('created_at')}</label>
                            <p className="text-sm">
                              {formatDateTime(event.translations[localeItem.fullCode].createdAt, locale)}
                            </p>
                          </div>

                          <div className="space-y-2">
                            <label className="text-muted-foreground text-sm font-medium">{t('updated_at')}</label>
                            <p className="text-sm">
                              {formatDateTime(event.translations[localeItem.fullCode].updatedAt, locale)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <div className="text-muted-foreground flex items-center justify-center gap-2">
                        <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={20} />
                        <span>{localeItem.name}</span>
                      </div>
                      <p className="text-muted-foreground mt-2">{t('no_translation_available')}</p>
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          ) : (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">{t('no_languages_available')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hero Picture */}
      <ContentDetailPicture picture={event.heroPicture} title="hero_picture" />

      {/* Pictures */}
      <ContentDetailPictures pictures={event.pictures} />

      {/* Files */}
      <ContentDetailFiles files={event.files} />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={!!deleteEventId}
        onConfirm={confirmDeleteEvent}
        onCancel={() => setDeleteEventId(null)}
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default Detail
